import { FC, useEffect, useRef, useState } from "react";
import "./ModalArticleVersionInsert.scss";
import ModalDefault from "../../../components/ModalDefault/ModalDefault";
import {
  ArticlesProvider,
  useArticles,
} from "../../../providers/Articles/ArticlesProvider";
import { Card, Col, Row } from "react-bootstrap";
import { VersionModel } from "../../../models/Articles/VersionModel";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import ReactMarkdown from "react-markdown";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

interface ModalArticleVersionInsertProps {
  show: boolean;
  onClose: () => void;
  articleId: number;
}

const ModalArticleVersionInsert: FC<ModalArticleVersionInsertProps> = ({
  show,
  onClose,
  articleId,
}) => {
  const [step, setStep] = useState<number>(1);
  const [stepInternalUser, setStepInternalUser] = useState<number>(1);
  const [stepInternalIA, setStepInternalIA] = useState<number>(1);
  const [stepPrompt, setStepPrompt] = useState<number>(1);
  const [selectedLogId, setSelectedLogId] = useState<number>(0);
  const [currentLogId, setCurrentLogId] = useState<number>(0);
  const [htmlContent, setHtmlContent] = useState<any>("");

  const {
    handleListVersion,
    handleRestoreVersion,
    handleList,
    isLoadingVersion,
    isLoadingRestoreVersion,
    articleVersion,
    setError,
    setIsLoadingVersion,
  } = useArticles();

  const handleClose = () => {
    setStep(1);
    setStepInternalUser(1);
    setStepInternalIA(1);
    setStepPrompt(1);
    setError("");
    onClose();
  };

  useEffect(() => {
    if (show) {
      if (articleId > 0) {
        handleListVersion({
          article_id: articleId,
          page: 1,
          perPage: 10,
          withPaginate: false,
        });
      }
    } else {
      setStep(1);
      setStepInternalUser(1);
      setStepInternalIA(1);
      setStepPrompt(1);
      setSelectedLogId(0);
      setCurrentLogId(0);
      setHtmlContent("");
      setIsLoadingVersion(false);
    }
  }, [show]);

  useEffect(() => {
    if (!articleVersion?.length) return;

    const parse = (s: any) => {
      const formats = [
        "DD/MM/YYYY HH:mm:ss",
        "DD/MM/YYYY HH:mm",
        "DD/MM/YYYY",
        moment.ISO_8601,
      ];
      const m = moment(s, formats, true);
      return m.isValid() ? m : moment(s);
    };

    const moments = articleVersion.map((item) => parse(item.updated_at));
    const maxMoment = moment.max(moments);

    const mostRecent = articleVersion.find(
      (item) => parse(item.updated_at).valueOf() === maxMoment.valueOf(),
    );

    setHtmlContent(mostRecent);
    setSelectedLogId(mostRecent?.log_id ?? 0);
    setCurrentLogId(mostRecent?.log_id ?? 0);
    setStep(1);
    setStepInternalUser(1);
    setStepInternalIA(1);
    setStepPrompt(1);
  }, [articleVersion]);

  useEffect(() => {
    if (!selectedLogId) return;

    const el = document.getElementById(`card-${selectedLogId}`);

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedLogId]);

  const handleStep = (step: number) => {
    setStep(step);
  };

  const handleSelectedVersion = (logId: number) => {
    if (logId > 0) {
      setSelectedLogId(logId);
      const filContent = articleVersion.filter(
        (x: VersionModel) => x?.log_id === logId,
      );

      setHtmlContent(filContent[0]);
    }
  };

  const getPreviousVersion = () => {
    if (!selectedLogId || !articleVersion?.length) return null;

    const sortedVersions = [...articleVersion].sort(
      (a, b) => moment(a.updated_at).valueOf() - moment(b.updated_at).valueOf(),
    );

    const index = sortedVersions.findIndex((x) => x.log_id === selectedLogId);

    if (index <= 0) return null;

    return sortedVersions[index - 1];
  };

  const canRestore = selectedLogId > 0 && selectedLogId !== currentLogId;

  const handleRestoreClick = () => {
    if (!canRestore) return;

    const formattedDate = moment(htmlContent?.updated_at).format(
      "DD/MM/YYYY HH:mm:ss",
    );

    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Você está prestes a restaurar a versão de ${formattedDate} gravada por ${htmlContent?.user}. Isso vai substituir o conteúdo atual (que ficará disponível como uma nova entrada no histórico). Deseja continuar?</span>`,
      icon: "question",
      confirmButtonText: "Sim, restaurar!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (!x.isConfirmed) return;

      const ret = await handleRestoreVersion({
        article_id: articleId,
        log_id: selectedLogId,
      });

      if (ret) {
        toast.success("Versão restaurada com sucesso!");
        handleListVersion({
          article_id: articleId,
          page: 1,
          perPage: 10,
          withPaginate: false,
        });
        handleList();
      } else {
        await Swal.fire(
          "Ops!",
          "Não foi possível restaurar a versão do artigo.",
          "error",
        );
      }
    });
  };

  const generateHtmlDiff = (oldHtml: string, newHtml: string) => {
    const parser = new DOMParser();

    const oldDoc = parser.parseFromString(oldHtml, "text/html");
    const newDoc = parser.parseFromString(newHtml, "text/html");

    const oldBlocks = Array.from(oldDoc.body.children);
    const newBlocks = Array.from(newDoc.body.children);

    return newBlocks
      .map((block, index) => {
        const oldBlock = oldBlocks[index];

        if (!oldBlock) {
          return `<div style="background:#d4fcbc;padding:6px;border-radius:4px;">${block.outerHTML}</div>`;
        }

        if (oldBlock.outerHTML !== block.outerHTML) {
          return `<div style="background:#d6ffcb;padding:6px;border-radius:4px;">${block.outerHTML}</div>`;
        }

        return block.outerHTML;
      })
      .join("");
  };

  const renderMenuInternal = (setInfo: any, info: any) => {
    return (
      <div className="d-flex align-items-center mb-4">
        <div className="menu-internal">
          <a
            onClick={() => setInfo(1)}
            className={`${info === 1 ? "activeInternal" : ""}`}
            href="#"
          >
            Conteúdo
          </a>
        </div>
        <span className="ms-1 me-1">|</span>
        <div className="menu-internal">
          <a
            onClick={() => setInfo(2)}
            className={`${info === 2 ? "activeInternal" : ""}`}
            href="#"
          >
            Comparação
          </a>
        </div>
      </div>
    );
  };

  const renderArticle = () => {
    return (
      <>
        {renderMenuInternal(setStepInternalUser, stepInternalUser)}

        {stepInternalUser === 1 ? (
          <div className="content">
            {!isLoadingVersion ? (
              <Card className="content-card" style={{ height: "500px" }}>
                <Card.Body>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: htmlContent?.previous_content ?? "",
                    }}
                  />
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body>
                  <Skeleton className="" height={450} width={655} />
                </Card.Body>
              </Card>
            )}
          </div>
        ) : (
          <div className="content">{renderComparison()}</div>
        )}
      </>
    );
  };

  const renderInfo = () => {
    return (
      <>
        {!isLoadingVersion ? (
          <Row className="content-card mt-5">
            <Col md={12}>
              <div className="d-flex justify-content-end flex-column align-items-end">
                {articleVersion.map((x: VersionModel) => (
                  <Card
                    id={`card-${x.log_id}`}
                    onClick={() => handleSelectedVersion(x?.log_id ?? 0)}
                    className={`mb-2 card-info ${
                      selectedLogId === x?.log_id ? "card-selected" : ""
                    }`}
                  >
                    <Card.Body>
                      <b className="me-1">
                        {moment(x?.updated_at).format("DD/MM/YYYY")}
                      </b>
                      |
                      <b className="ms-1">
                        {moment(x?.updated_at).format("HH:mm:ss")}
                      </b>
                      <div className="mt-2 text-user">
                        <b>Usuário:</b> {x?.user}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Col>
          </Row>
        ) : (
          <div className="mt-5">
            <Card>
              <Card.Body>
                <Skeleton height={15} width={200} />
                <div className="d-flex mt-2">
                  <Skeleton height={15} width={50} />
                  <Skeleton className="ms-2" height={15} width={140} />
                </div>
              </Card.Body>
            </Card>
            <Card className="mt-2">
              <Card.Body>
                <Skeleton height={15} width={200} />
                <div className="d-flex mt-2">
                  <Skeleton height={15} width={50} />
                  <Skeleton className="ms-2" height={15} width={140} />
                </div>
              </Card.Body>
            </Card>
            <Card className="mt-2">
              <Card.Body>
                <Skeleton height={15} width={200} />
                <div className="d-flex mt-2">
                  <Skeleton height={15} width={50} />
                  <Skeleton className="ms-2" height={15} width={140} />
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </>
    );
  };

  const renderContentIa = () => {
    return (
      <>
        {renderMenuInternal(setStepInternalIA, stepInternalIA)}
        {stepInternalIA === 1 ? (
          <div>
            {!isLoadingVersion && (
              <Card className="content-card">
                <Card.Body>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: htmlContent?.previous_content_ia ?? "",
                    }}
                  />
                </Card.Body>
              </Card>
            )}
          </div>
        ) : (
          <>{renderComparisonIa()}</>
        )}
      </>
    );
  };

  const renderComparison = () => {
    const previousVersion = getPreviousVersion();
    const currentVersion = htmlContent;

    if (!previousVersion) {
      return (
        <Row>
          <Col md={12}>
            <Card
              className="d-flex align-items-center"
              style={{ height: "500px" }}
            >
              <Card.Body>
                <strong>
                  <p>Não existe versão anterior</p>
                </strong>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    const diffContent = generateHtmlDiff(
      previousVersion?.previous_content ?? "",
      currentVersion?.previous_content ?? "",
    );

    return (
      <div>
        <Row style={{ height: "500px", overflowY: "auto" }}>
          <Col md={6}>
            <Card style={{ overflowX: "auto" }}>
              <Card.Body>
                <h4 className="mb-4">Anterior</h4>

                <div
                  dangerouslySetInnerHTML={{
                    __html: previousVersion?.previous_content ?? "",
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card style={{ overflowX: "auto" }}>
              <Card.Body>
                <h4 className="mb-4">Atual</h4>

                <div
                  dangerouslySetInnerHTML={{
                    __html: diffContent,
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderComparisonIa = () => {
    const previousVersion = getPreviousVersion();
    const currentVersion = htmlContent;

    if (!previousVersion) {
      return (
        <Row>
          <Col md={12}>
            <Card
              className="d-flex align-items-center"
              style={{ height: "500px" }}
            >
              <Card.Body>
                <strong>
                  <p>Não existe versão anterior</p>
                </strong>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    const diffContent = generateHtmlDiff(
      previousVersion?.previous_content_ia ?? "",
      currentVersion?.previous_content_ia ?? "",
    );

    return (
      <Row style={{ height: "500px", overflowY: "auto" }}>
        <Col md={6}>
          <Card style={{ overflowX: "auto" }}>
            <Card.Body>
              <h4 className="mb-4">IA - Anterior</h4>

              <div
                dangerouslySetInnerHTML={{
                  __html: previousVersion?.previous_content_ia ?? "",
                }}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card style={{ overflowX: "auto" }}>
            <Card.Body>
              <h4 className="mb-4">IA - Atual</h4>

              <div
                dangerouslySetInnerHTML={{
                  __html: diffContent,
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderPrompt = () => {
    return (
      <div className="mt-5">
        {!isLoadingVersion && (
          <Card className="content-card">
            <Card.Body>
              <ReactMarkdown>
                {htmlContent?.previous_prompt ?? ""}
              </ReactMarkdown>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  type SpreadsheetStatus =
    | "Adicionado"
    | "Alterado"
    | "Removido"
    | "Inalterado";

  const getSpreadsheetRows = (): {
    slot: number;
    name: string;
    status: SpreadsheetStatus;
  }[] => {
    const current: string[] = htmlContent?.spreadsheet_names ?? [];
    const previousVersion = getPreviousVersion();
    const previous: string[] = previousVersion?.spreadsheet_names ?? [];

    const maxLength = Math.max(current.length, previous.length);
    const rows: { slot: number; name: string; status: SpreadsheetStatus }[] =
      [];

    for (let i = 0; i < maxLength; i++) {
      const currName = current[i];
      const prevName = previous[i];

      if (currName && !prevName) {
        rows.push({
          slot: i + 1,
          name: currName,
          status: previousVersion ? "Adicionado" : "Inalterado",
        });
      } else if (!currName && prevName) {
        rows.push({ slot: i + 1, name: prevName, status: "Removido" });
      } else if (currName && prevName && currName !== prevName) {
        rows.push({ slot: i + 1, name: currName, status: "Alterado" });
      } else if (currName && prevName) {
        rows.push({ slot: i + 1, name: currName, status: "Inalterado" });
      }
    }

    return rows;
  };

  const renderSpreadsheets = () => {
    const rows = getSpreadsheetRows();

    return (
      <div className="mt-5">
        {!isLoadingVersion && (
          <Card className="content-card">
            <Card.Body>
              {rows.length ? (
                <table className="spreadsheet-table">
                  <thead>
                    <tr>
                      <th>Arquivo</th>
                      <th>Nome</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${htmlContent?.log_id}-spreadsheet-${row.slot}`}>
                        <td>Arquivo {row.slot}</td>
                        <td>
                          <i className="fas fa-file-excel me-2"></i>
                          {row.name}
                        </td>
                        <td>
                          <span
                            className={`spreadsheet-status spreadsheet-status--${row.status.toLowerCase()}`}
                          >
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="mb-0">
                  Nenhuma planilha vinculada nesta versão.
                </p>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  const renderMenu = () => {
    return (
      <ul className="nav nav-tabs mb-4 mt-3">
        <li className="nav-item">
          <a
            onClick={() => handleStep(1)}
            className={`nav-link ${step === 1 ? "active" : ""}`}
            href="#"
          >
            Conteúdo Usuário
          </a>
        </li>
        <li className="nav-item">
          <a
            onClick={() => handleStep(2)}
            className={`nav-link ${step === 2 ? "active" : ""}`}
            href="#"
          >
            Conteúdo IA
          </a>
        </li>
        <li className="nav-item">
          <a
            onClick={() => handleStep(3)}
            className={`nav-link ${step === 3 ? "active" : ""}`}
            href="#"
          >
            Prompt
          </a>
        </li>
        <li className="nav-item">
          <a
            onClick={() => handleStep(4)}
            className={`nav-link ${step === 4 ? "active" : ""}`}
            href="#"
          >
            Planilhas
          </a>
        </li>
      </ul>
    );
  };

  return (
    <ModalDefault
      title="Histórico"
      icon="article"
      show={show}
      onClose={handleClose}
      sizeModal={"xxl"}
      showFooter={true}
      backdrop="static"
      backgroundColor="#f8f9fa"
      handleSubmit={handleRestoreClick}
      buttonText="Restaurar"
      disabledSubmit={!canRestore || isLoadingRestoreVersion}
      showButtonSpinner={isLoadingRestoreVersion}
    >
      <div
        className="ModalArticleVersionInsert"
        data-testid="ModalArticleVersionInsert"
      >
        <>
          {renderMenu()}

          <Row>
            <Col md={8}>
              {step === 1
                ? renderArticle()
                : step === 2
                  ? renderContentIa()
                  : step === 3
                    ? renderPrompt()
                    : renderSpreadsheets()}
            </Col>
            <Col md={4}>{renderInfo()}</Col>
          </Row>
        </>
      </div>
    </ModalDefault>
  );
};

export default ModalArticleVersionInsert;
